import type { DocNode } from '@atlaskit/adf-schema/doc';

import { buildContentAri, type BylineContentType } from '../utils/contentAri.ts';
import { extractSmartLinkSourceExcerptsFromAdf } from '../utils/extractSmartLinkSourceExcerptsFromAdf.ts';
import type { EnhancedSmartLink } from '../utils/smartLinkTypes.ts';

import type { AtAGlanceFeatureInput, AtAGlanceStructuralAncestor } from './atAGlanceTypes.ts';
import { extractAdfExcerpt } from './extractAdfExcerpt.ts';
import { bound, toIsoInstant, AT_A_GLANCE_INPUT_LIMITS } from './utils/atAGlanceInputFields.ts';
import { collectEntities, structuralParentEntityFor } from './utils/buildAtAGlanceEntities.ts';
import { mergeAtAGlanceEvidence } from './utils/mergeAtAGlanceEvidence.ts';

// AFM's isolated declaration build requires explicit types for these exported aliases.
export const TITLE_LIMIT: typeof AT_A_GLANCE_INPUT_LIMITS.TITLE_LIMIT =
	AT_A_GLANCE_INPUT_LIMITS.TITLE_LIMIT;
export const ENTITY_SUMMARY_LIMIT: typeof AT_A_GLANCE_INPUT_LIMITS.ENTITY_SUMMARY_LIMIT =
	AT_A_GLANCE_INPUT_LIMITS.ENTITY_SUMMARY_LIMIT;
export const ENTITY_STATUS_LIMIT: typeof AT_A_GLANCE_INPUT_LIMITS.ENTITY_STATUS_LIMIT =
	AT_A_GLANCE_INPUT_LIMITS.ENTITY_STATUS_LIMIT;
export const PRODUCT_LIMIT: typeof AT_A_GLANCE_INPUT_LIMITS.PRODUCT_LIMIT =
	AT_A_GLANCE_INPUT_LIMITS.PRODUCT_LIMIT;
export const RESOURCE_TYPE_LIMIT: typeof AT_A_GLANCE_INPUT_LIMITS.RESOURCE_TYPE_LIMIT =
	AT_A_GLANCE_INPUT_LIMITS.RESOURCE_TYPE_LIMIT;

export const LOCALE_LIMIT = 35;

type BuildAtAGlanceInputOptions = {
	cloudId: string;
	contentId: string;
	/** Selects the `content_ari` the request names. Undefined means page. */
	contentType?: BylineContentType;
	locale: string;
	pageTitle: string;
	pageVersion?: number | null;
	pageUpdatedAt?: string | null;
	pageAdf: DocNode;
	structuralAncestors?: readonly AtAGlanceStructuralAncestor[] | null;
	incomingLinks: EnhancedSmartLink[];
	outgoingLinks: EnhancedSmartLink[];
	relatedContentLinks?: EnhancedSmartLink[];
};

const toPositiveContentVersion = (value: number | null | undefined): number | undefined =>
	typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : undefined;

/**
 * Builds the cc-smarts `twg-hub-summaries` request from the modal's connection
 * data and the current page ADF. A page with text can request an overview even
 * when none of its connections can be sent as linked entities.
 */
export const buildAtAGlanceInput = ({
	cloudId,
	contentId,
	contentType,
	locale,
	pageTitle,
	pageVersion,
	pageUpdatedAt,
	pageAdf,
	structuralAncestors,
	incomingLinks,
	outgoingLinks,
	relatedContentLinks = [],
}: BuildAtAGlanceInputOptions): AtAGlanceFeatureInput | null => {
	const normalizedLocale = locale.trim();
	const title = bound(pageTitle, TITLE_LIMIT);
	if (!normalizedLocale || normalizedLocale.length > LOCALE_LIMIT || !title) {
		return null;
	}

	const { excerpt, truncated } = extractAdfExcerpt(pageAdf);
	if (!excerpt) {
		return null;
	}

	const sourceExcerpts =
		outgoingLinks.length > 0
			? extractSmartLinkSourceExcerptsFromAdf(pageAdf)
			: new Map<string, string>();
	const incoming = collectEntities(incomingLinks, 'appears_in', sourceExcerpts);
	const outgoing = collectEntities(outgoingLinks, 'links_to', sourceExcerpts);
	const related = collectEntities(relatedContentLinks, 'related_content', sourceExcerpts);
	const structuralParent = structuralParentEntityFor(cloudId, structuralAncestors);
	// Every deduplicated entity is sent. cc-smarts reconciles this inventory with its own
	// unbounded graph discovery, and it cannot recover what is dropped here: incoming-only
	// entities, related content, ancestors, and outgoing links whose graph type its discovery
	// does not decode all reach the model only via this list.
	//
	// The order is evidence strength, not priority for a cap: related content (the only
	// collection whose one-liners the UI renders), then the structural parent, then links the
	// page makes explicitly, then references to it. `mergeAtAGlanceEvidence` keeps an entity at its first
	// occurrence, so the order is deterministic and the request body stays a stable cache key.
	const linkedEntities = mergeAtAGlanceEvidence([
		...related,
		...(structuralParent ? [structuralParent] : []),
		...outgoing,
		...incoming,
	]);
	const version = toPositiveContentVersion(pageVersion);
	const updatedAt = toIsoInstant(pageUpdatedAt);

	return {
		content_ari: buildContentAri(cloudId, contentId, contentType),
		locale: normalizedLocale,
		central_page: {
			title,
			mime_type: 'text/markdown',
			excerpt,
			truncated,
			...(version ? { version } : {}),
			...(updatedAt ? { updated_at: updatedAt } : {}),
		},
		linked_entities: linkedEntities,
	};
};
